import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function FuturesListDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="List - Futures API" description="Query list data via the Futures API; request parameters, code examples and the response schema for the list endpoint."  canonicalUrl="https://axionquant.com/docs/01-market-data/futures/list" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">List - Futures API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>

        {/* List by Exchange */}
        <div id="exchange" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /futures/list/exchange
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique exchange values available in the futures universe. Filter by exchange code.
              </p>
            </div>

            <Links links={['/futures/list/exchange']} />

          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Exchange" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Exchange</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="futures.list(exchange)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/futures/list-exchange"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Currency */}
        <div id="currency" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /futures/list/currency
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique currency values available in the futures universe. Filter by trading currency.
              </p>
            </div>

            <Links links={['/futures/list/currency']} />

          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Currency" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Currency</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="futures.list(currency)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/futures/list-currency"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Timezone */}
        <div id="timezone" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /futures/list/timezone
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique timezone values available in the futures universe. Filter by timezone.
              </p>
            </div>

            <Links links={['/futures/list/timezone']} />

          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Timezone" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Timezone</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="futures.list(timezone)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/futures/list-timezone"></Code>
              </div>
            </div>
          </div>
        </div>
        {/* List by Country */}
        <div id="country" className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /futures/list/country
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves all unique country values available in the futures universe. Filter by country name or code.
              </p>
            </div>

            <Links links={['/futures/list/country']} />

          </div>
          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="List by Country" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">List by Country</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="futures.list(country)"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="01-market-data/futures/list-country"></Code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
